import React, { useState } from 'react';
import { Modal, LockboxModalHeader } from '../ui';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import { TextInput } from '../ui/Input';
import { searchUsersByUsername } from '../../api/userProfileService';
import { sendGroupInvite } from '../../api/groupService';
import { getGroupInviteErrorMessage } from '../../utils/groupInviteErrors';
import ShareLeagueLinkCard from './ShareLeagueLinkCard';

export default function InviteMemberModal({
  open,
  onClose,
  groupId,
  groupName,
  fromDisplayName,
  onInvited,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    const q = query.trim();
    if (q.length < 2) {
      setError('Enter at least 2 characters');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const users = await searchUsersByUsername(q);
      setResults(users || []);
    } catch (err) {
      setError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (user) => {
    setLoading(true);
    setError('');
    try {
      await sendGroupInvite({
        groupId,
        groupName,
        toUserId: user.id,
        fromDisplayName,
      });
      onInvited?.(user);
      onClose();
    } catch (err) {
      setError(getGroupInviteErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      accent="admin"
      header={(
        <LockboxModalHeader
          emoji="👥"
          iconClassName="bg-violet-500/10 border-violet-500/20"
          title="Invite member"
          onClose={onClose}
        />
      )}
    >
      <div className="space-y-4">
        <ShareLeagueLinkCard groupId={groupId} groupName={groupName} />
        <p className="text-[10px] uppercase tracking-widest text-gray-600 text-center">or invite by username</p>
        <div className="flex gap-2">
          <TextInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search username"
            className="flex-1 mt-0"
          />
          <Button type="button" size="sm" variant="admin" disabled={loading} onClick={handleSearch}>
            Search
          </Button>
        </div>
        {error && <Alert tone="error" className="text-xs font-bold">{error}</Alert>}
        <ul className="space-y-2 max-h-48 overflow-y-auto">
          {results.map((user) => (
            <li
              key={user.id}
              className="flex items-center justify-between gap-2 rounded-xl border border-gray-800 bg-black/40 px-3 py-2"
            >
              <span className="text-sm font-bold truncate">{user.name || user.username}</span>
              <Button
                type="button"
                size="sm"
                variant="admin"
                disabled={loading}
                onClick={() => handleInvite(user)}
              >
                Invite
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
